import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Table, Modal, message, Popconfirm, Form, Input, Select, InputNumber, Space, Button, Card, Divider } from 'antd';
import { SuperThemProcessManagementPageModel } from '../pageModels/SuperThemProcessManagementPageModel';
import { ProcessStep } from '../../models/SuperThemProcess';
import '../styles/SuperThemProcessManagement.css';

const SuperThemProcessManagement: React.FC = observer(() => {
  const [pageModel] = useState(() => new SuperThemProcessManagementPageModel());

  useEffect(() => {
    pageModel.loadProcessSteps();
  }, []);

  const handleAdd = () => {
    pageModel.showAddModal();
  };

  const handleEdit = (step: ProcessStep) => {
    pageModel.showEditModal(step);
  };

  const handleDelete = async (id: string) => {
    await pageModel.removeProcessStep(id);
    message.success('删除成功');
  };

  const handleSave = async () => {
    if (!pageModel.editingStep?.title.trim()) {
      message.error('请输入步骤标题');
      return;
    }
    if (!pageModel.editingStep?.content.trim()) {
      message.error('请输入步骤内容');
      return;
    }
    if (pageModel.editingStep?.type === 'input' && !pageModel.editingStep?.requiredText?.trim()) {
      message.error('请输入手写文案');
      return;
    }
    if (!pageModel.editingStep?.confirmButtonText?.trim()) {
      message.error('请输入确认按钮文案');
      return;
    }

    await pageModel.saveProcessStep();
    message.success(pageModel.isEditMode ? '编辑成功' : '添加成功');
  };

  const columns = [
    {
      title: '步骤标题',
      dataIndex: 'title',
      key: 'title',
      width: 150,
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 90,
      render: (type: string) => (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '3px 10px',
          borderRadius: '0',
          fontSize: '12px',
          fontWeight: '500',
          color: 'white',
          background: type === 'agree' ? '#52c41a' : '#1890ff',
          border: 'none'
        }}>
          {type === 'agree' ? '同意型' : '输入型'}
        </div>
      ),
    },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      width: 180,
      ellipsis: true,
    },
    {
      title: '手写文案',
      dataIndex: 'requiredText',
      key: 'requiredText',
      width: 130,
      render: (text: string) => text || '-',
    },
    {
      title: '顺序',
      dataIndex: 'order',
      key: 'order',
      width: 70,
    },
    {
      title: '确认文案',
      dataIndex: 'confirmButtonText',
      key: 'confirmButtonText',
      width: 80,
    },
    {
      title: '取消文案',
      dataIndex: 'cancelButtonText',
      key: 'cancelButtonText',
      width: 80,
      render: (text: string) => text || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 110,
      render: (_: any, record: ProcessStep) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            onClick={() => handleEdit(record)}
            style={{ 
              borderRadius: '0',
              background: '#faad14',
              border: 'none',
              fontSize: '11px',
              height: '26px',
              padding: '0 10px'
            }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个流程步骤吗？"
            description="删除后将无法恢复，请谨慎操作"
            onConfirm={() => handleDelete(record.id)}
            okText="确定删除"
            cancelText="取消"
            okType="danger"
          >
            <Button
              danger
              size="small"
              style={{ 
                borderRadius: '0',
                fontSize: '11px',
                height: '26px',
                padding: '0 10px'
              }}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="super-them-process-container">
      <div className="super-them-process-header"> 
        <Button
          type="primary"
          size="middle"
          onClick={handleAdd}
          style={{ 
            borderRadius: '0',
            height: '36px',
            padding: '0 18px',
            fontSize: '14px',
            background: '#1890ff',
            border: 'none',
            boxShadow: '0 2px 4px rgba(24,144,255,0.3)'
          }}
        >
          新增流程步骤
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={pageModel.processSteps}
        rowKey="id"
        loading={pageModel.loading}
        pagination={false}
        scroll={{ x: 1100 }}
        style={{ borderRadius: '0' }}
        className="process-steps-table"
        onRow={(record) => ({
          onClick: () => handleEdit(record),
          style: { cursor: 'pointer' }
        })}
      />

      <Modal
        title={
          <div style={{ textAlign: 'center', fontSize: '18px', fontWeight: '500' }}>
            {pageModel.isEditMode ? '编辑流程步骤' : '新增流程步骤'}
          </div>
        }
        open={pageModel.isModalVisible}
        onCancel={() => pageModel.hideModal()}
        footer={null}
        width={650}
        className="process-step-modal"
        destroyOnClose
      >
        <Card style={{ border: 'none', boxShadow: 'none', padding: '0' }}>
          <Form layout="vertical" size="middle">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item 
                label={<span style={{ fontWeight: '500', color: '#333' }}>步骤标题 <span style={{ color: '#ff4d4f' }}>*</span></span>}
                required
              >
                <Input
                  value={pageModel.editingStep?.title || ''}
                  onChange={(e) => pageModel.updateEditingStep('title', e.target.value)}
                  placeholder="请输入步骤标题"
                  style={{ borderRadius: '0' }}
                />
              </Form.Item>

              <Form.Item 
                label={<span style={{ fontWeight: '500', color: '#333' }}>步骤类型 <span style={{ color: '#ff4d4f' }}>*</span></span>}
                required
              >
                <Select
                  value={pageModel.editingStep?.type || 'agree'}
                  onChange={(value) => pageModel.updateEditingStep('type', value)}
                  style={{ borderRadius: '0' }}
                  options={[
                    { label: '同意型', value: 'agree' },
                    { label: '输入型', value: 'input' }
                  ]}
                />
              </Form.Item>
            </div>

            <Form.Item 
              label={<span style={{ fontWeight: '500', color: '#333' }}>步骤内容 <span style={{ color: '#ff4d4f' }}>*</span></span>}
              required
            >
              <Input.TextArea
                value={pageModel.editingStep?.content || ''}
                onChange={(e) => pageModel.updateEditingStep('content', e.target.value)}
                placeholder="请输入步骤内容描述"
                rows={4}
                style={{ borderRadius: '0' }}
              />
            </Form.Item>

            {pageModel.editingStep?.type === 'input' && (
              <Form.Item 
                label={<span style={{ fontWeight: '500', color: '#333' }}>手写文案 <span style={{ color: '#ff4d4f' }}>*</span></span>}
                required
              >
                <Input
                  value={pageModel.editingStep?.requiredText || ''}
                  onChange={(e) => pageModel.updateEditingStep('requiredText', e.target.value)}
                  placeholder="请输入需要用户手写的文案"
                  style={{ borderRadius: '0' }}
                />
              </Form.Item>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: '16px' }}>
              <Form.Item 
                label={<span style={{ fontWeight: '500', color: '#333' }}>显示顺序</span>}
              >
                <InputNumber
                  value={pageModel.editingStep?.order || 1}
                  onChange={(value) => pageModel.updateEditingStep('order', value || 1)}
                  min={1}
                  style={{ width: '100%', borderRadius: '0' }}
                  placeholder="顺序"
                />
              </Form.Item>

              <Form.Item 
                label={<span style={{ fontWeight: '500', color: '#333' }}>确认按钮文案 <span style={{ color: '#ff4d4f' }}>*</span></span>}
                required
              >
                <Input
                  value={pageModel.editingStep?.confirmButtonText || ''}
                  onChange={(e) => pageModel.updateEditingStep('confirmButtonText', e.target.value)}
                  placeholder="如：同意、确认、下一步"
                  style={{ borderRadius: '0' }}
                />
              </Form.Item>

              <Form.Item 
                label={<span style={{ fontWeight: '500', color: '#333' }}>取消按钮文案</span>}
              >
                <Input
                  value={pageModel.editingStep?.cancelButtonText || ''}
                  onChange={(e) => pageModel.updateEditingStep('cancelButtonText', e.target.value)}
                  placeholder="如：不同意、取消、返回（可选）"
                  style={{ borderRadius: '0' }}
                />
              </Form.Item>
            </div>
          </Form>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <Button 
                size="middle"
                onClick={() => pageModel.hideModal()}
                style={{ 
                  borderRadius: '0',
                  padding: '6px 20px',
                  height: '36px',
                  border: '1px solid #d9d9d9',
                  color: '#666'
                }}
              >
                取消
              </Button>
              <Button 
                type="primary" 
                size="middle"
                onClick={handleSave}
                style={{ 
                  borderRadius: '0',
                  padding: '6px 20px',
                  height: '36px',
                  background: '#1890ff'
                }}
              >
                保存
              </Button>
            </div>
        </Card>
      </Modal>
    </div>
  );
});

export default SuperThemProcessManagement; 